/* PHASES marketing popup (T-390, snippets/phases-marketing-popup.liquid)
 * Opens after the set delay on store pages (never over the Locksmith gate), at most once per visitor:
 * - closing it hides it for 14 days; submitting it, or any other signup form, hides it for good;
 * - step 1 (email) is sent to Customer.io by assets/phases-signup-forms.js, then step 2 (phone + SMS consent)
 *   is shown with the email carried over; closing step 2 keeps the step 1 signup and shows the success message;
 * - Escape, the X or a click outside closes it, and focus stays inside while it is open.
 */
(() => {
  if (customElements.get("phases-marketing-popup")) return;

  const KEY = "phasesMarketingPopup";
  const SNOOZE_DAYS = 14;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])';

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || "null");
    } catch (e) {
      return null;
    }
  };
  const write = (value) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(value));
    } catch (e) {}
  };
  const hiddenForNow = () => {
    const state = read();
    if (!state) return false;
    if (state.done) return true;
    return Date.now() < (state.until || 0);
  };

  // A valid US number: 10 digits, optionally +1, 1 or 001 first. Area code and exchange can't start with 0 or 1.
  // Customer.io silently drops numbers it can't use, so this has to catch them while the visitor can still fix it.
  const validPhone = (raw) => {
    const value = String(raw || "").trim();
    let digits = value.replace(/\D/g, "");
    if (digits.startsWith("001")) digits = digits.slice(2);
    if ((value.startsWith("+") || value.startsWith("00") || digits.length === 11) && !digits.startsWith("1"))
      return false;
    if (digits.length === 11) digits = digits.slice(1);
    return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
  };

  // A signup anywhere on the site (footer, mobile menu, waitlist) means the popup isn't needed again. Valid
  // submissions only: phases-signup-forms.js stops invalid ones in the capture phase before this runs.
  document.addEventListener("submit", (event) => {
    if (event.target.closest?.("[data-phases-signup-form]")) write({ done: true });
  });

  customElements.define(
    "phases-marketing-popup",
    class extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector(".phases-mpopup__dialog");
        this.steps = this.querySelectorAll("[data-mpopup-step]");
        this.emailForm = this.querySelector('[data-mpopup-form="email"]');
        this.phoneForm = this.querySelector('[data-mpopup-form="phone"]');
        this.step = "1";

        this.querySelectorAll("[data-mpopup-close]").forEach((el) => el.addEventListener("click", () => this.close()));
        this.addEventListener("keydown", (event) => this.onKeydown(event));

        // step 2 needs a valid phone; stop the submission before it is sent (capture phase on the form)
        this.phoneForm?.addEventListener(
          "submit",
          (event) => {
            const phone = this.phoneForm.querySelector('input[name="phone"]');
            const error = this.phoneForm.querySelector("[data-mpopup-phone-error]");
            if (validPhone(phone?.value)) {
              if (error) error.hidden = true;
              phone?.removeAttribute("aria-invalid");
              return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            if (error) error.hidden = false;
            phone?.setAttribute("aria-invalid", "true");
            phone?.focus();
          },
          true,
        );
        this.phoneForm?.addEventListener("input", (event) => {
          if (event.target.name !== "phone") return;
          const error = this.phoneForm.querySelector("[data-mpopup-phone-error]");
          if (error) error.hidden = true;
          event.target.removeAttribute("aria-invalid");
        });

        // after a valid submission (phases-signup-forms.js sends it to Customer.io on window), move on
        this.emailForm?.addEventListener("submit", () => {
          const email = this.emailForm.querySelector('input[name="email"]')?.value || "";
          const carried = this.phoneForm?.querySelector("[data-mpopup-email]");
          if (carried) carried.value = email.trim();
          setTimeout(() => this.show(this.phoneForm ? "2" : "done"));
        });
        this.phoneForm?.addEventListener("submit", () => setTimeout(() => this.show("done")));

        // theme editor: open straight away while the settings are being edited
        if (this.hasAttribute("data-design-mode")) {
          this.open();
          return;
        }
        if (hiddenForNow()) return;
        const seconds = Number(this.dataset.delay);
        const delay = (Number.isFinite(seconds) && seconds >= 0 ? seconds : 8) * 1000;
        this.timer = setTimeout(() => this.maybeOpen(), delay);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
      }

      // not over the Locksmith gate / passcode form, another open dialog, or after a signup since load
      maybeOpen() {
        if (hiddenForNow()) return;
        if (document.querySelector("[data-phases-access-gate], #locksmith_passcode_form, #locksmith-content")) return;
        if (document.querySelector("dialog[open], .drawer.is-open, [data-drawer].is-open")) {
          this.timer = setTimeout(() => this.maybeOpen(), 4000);
          return;
        }
        this.open();
      }

      open() {
        this.lastFocus = document.activeElement;
        this.hidden = false;
        requestAnimationFrame(() => this.classList.add("is-open"));
        document.documentElement.classList.add("phases-mpopup-open");
        this.focusFirst();
      }

      show(step) {
        this.step = step;
        this.steps.forEach((el) => (el.hidden = el.dataset.mpopupStep !== step));
        if (step === "done") {
          if (!this.hasAttribute("data-design-mode")) write({ done: true });
          this.querySelector('[data-mpopup-step="done"]')?.focus({ preventScroll: true });
        } else {
          this.focusFirst();
        }
      }

      close() {
        const editor = this.hasAttribute("data-design-mode");
        // closing step 2 keeps the email signup: say thanks first, the next close dismisses
        if (this.step === "2") {
          this.show("done");
          return;
        }
        if (this.step === "1" && !editor) write({ until: Date.now() + SNOOZE_DAYS * 864e5 });
        this.classList.remove("is-open");
        this.hidden = true;
        document.documentElement.classList.remove("phases-mpopup-open");
        this.lastFocus?.focus?.({ preventScroll: true });
      }

      focusFirst() {
        const step = this.querySelector(`[data-mpopup-step="${this.step}"]`);
        const field = step?.querySelector('input:not([type="hidden"])');
        (field || this.dialog)?.focus({ preventScroll: true });
      }

      onKeydown(event) {
        if (event.key === "Escape") {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== "Tab") return;
        const items = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
  );
})();
